import {
  Component,
  ElementRef,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  RouterModule,
  RouterOutlet,
  Router,
  NavigationEnd,
} from '@angular/router';
import { CommonModule } from '@angular/common';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { AuthService } from './auth.service';
import { FirebaseService } from './services/firebase.service';
import { Subscription, interval } from 'rxjs';
import { filter } from 'rxjs/operators';

declare const bootstrap: any;

interface BannerConfig {
  type: 'banner-warning' | 'banner-info' | 'banner-success' | 'banner-danger';
  icon: string;
  title: string;
  message: string;
  actionButton?: string;
  buttonClass?: string;
  buttonIcon?: string;
  actionRoute?: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Schedule Maker';

  // Banner notification system
  showBanner = false;
  showUpdateBanner = false;
  bannerConfig: BannerConfig = {
    type: 'banner-warning',
    icon: 'bi bi-exclamation-triangle-fill',
    title: '',
    message: '',
  };

  @ViewChild('navbarNav') navbarNav!: ElementRef;
  currentUser: any = null;
  hasIncompleteProfile: boolean = false;
  currentRoute: string = '';

  // Shift swap notification count
  pendingSwapCount: number = 0;

  // Subscription to track user changes
  private userSubscription?: Subscription;
  private countRefreshSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private updateSubscription?: Subscription;

  constructor(
    public auth: AuthService,
    private router: Router,
    private firebaseService: FirebaseService,
    private swUpdate: SwUpdate,
  ) {}

  async ngOnInit() {
    // console.log('AppComponent initialized');

    // Subscribe to router events to track current route
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.currentRoute = event.urlAfterRedirects || event.url;
        // console.log('📍 Current route:', this.currentRoute);

        // Re-evaluate banner when route changes
        this.evaluateBannerConditions();
      });

    // Subscribe to user changes from AuthService
    // This will trigger on:
    // - Initial page load (if user is already logged in)
    // - Login
    // - Logout
    // - Profile updates
    this.userSubscription = this.auth.user$.subscribe(async (user) => {
      // console.log('👤 User changed in AppComponent:', user);
      this.currentUser = user;

      // Re-evaluate profile and banners whenever user changes
      this.validateUserProfile();
      this.evaluateBannerConditions();

      // Load shift swap count
      if (user) {
        await this.loadPendingSwapCount();
      } else {
        this.pendingSwapCount = 0;
      }
    });

    // Refresh count every 30 seconds (optional)
    this.countRefreshSubscription = interval(30000).subscribe(() => {
      if (this.currentUser) {
        this.loadPendingSwapCount();
      }
    });

    if (this.swUpdate.isEnabled) {
      this.updateSubscription = this.swUpdate.versionUpdates
        .pipe(
          filter(
            (event): event is VersionReadyEvent =>
              event.type === 'VERSION_READY',
          ),
        )
        .subscribe(() => {
          this.showUpdateBanner = true;
        });
    }
  }

  ngOnDestroy() {
    // Clean up subscription to prevent memory leaks
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.countRefreshSubscription) {
      this.countRefreshSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
  }

  // ✅ NEW: Check if user is a regular employee (not spectator)
  get isEmployee(): boolean {
    if (!this.currentUser) return false;
    const role = this.currentUser.role?.toLowerCase();
    return role === 'employee';
  }

  // ✅ NEW: Check if user is a spectator
  get isSpectator(): boolean {
    if (!this.currentUser) return false;
    const role = this.currentUser.role?.toLowerCase();
    return role === 'spectator';
  }

  // Load pending shift swap count
  async loadPendingSwapCount() {
    if (!this.currentUser) {
      this.pendingSwapCount = 0;
      return;
    }

    try {
      const orgId = this.auth.getUsersOrgId();
      const employeeName = this.auth.getUserName();
      const isManager =
        this.currentUser.role === 'manager' ||
        this.currentUser.role === 'admin';

      this.pendingSwapCount =
        await this.firebaseService.getTotalPendingSwapCount(
          orgId,
          employeeName,
          isManager,
        );

      // console.log('🔔 Pending swap requests:', this.pendingSwapCount);
    } catch (error) {
      console.error('Error loading pending swap count:', error);
      this.pendingSwapCount = 0;
    }
  }

  clearUser() {
    this.auth.clearUser();
    this.router.navigate(['/login']);
    this.closeMenu();
  }

  closeMenu() {
    if (!this.navbarNav) return;
    const navbar = this.navbarNav.nativeElement;
    const bsCollapse = bootstrap.Collapse.getInstance(navbar);
    if (bsCollapse) bsCollapse.hide();
  }

  // Check if user profile is complete
  validateUserProfile() {
    if (!this.currentUser) {
      this.hasIncompleteProfile = false;
      return;
    }

    const user = this.currentUser;
    const hasFirstName = user.firstName && user.firstName.trim() !== '';
    const hasLastName = user.lastName && user.lastName.trim() !== '';
    const hasEmail = user.email && user.email.trim() !== '';
    const hasPassword = user.password && user.password.trim() !== '';

    this.hasIncompleteProfile = !(
      hasFirstName &&
      hasLastName &&
      hasEmail &&
      hasPassword
    );

    // console.log('📋 Profile validation:', {
    //   hasFirstName,
    //   hasLastName,
    //   hasEmail,
    //   hasPassword,
    //   isIncomplete: this.hasIncompleteProfile,
    // });
  }

  // Determine which banner to show based on conditions
  evaluateBannerConditions() {
    // Only show banners if user is logged in
    if (!this.currentUser) {
      this.showBanner = false;
      return;
    }

    // ✅ NEW: Don't show banner if user is already on the settings page
    if (this.currentRoute === '/settings') {
      this.showBanner = false;
      // console.log('🚫 Banner hidden - user is on settings page');
      return;
    }

    // Priority 1: Incomplete Profile
    if (this.hasIncompleteProfile) {
      this.showIncompleteProfileBanner();
      return;
    }

    // No banner needed
    this.showBanner = false;
  }

  // Show incomplete profile banner
  showIncompleteProfileBanner() {
    const missingItems: string[] = [];

    if (
      !this.currentUser.firstName ||
      this.currentUser.firstName.trim() === ''
    ) {
      missingItems.push('first name');
    }
    if (!this.currentUser.lastName || this.currentUser.lastName.trim() === '') {
      missingItems.push('last name');
    }
    if (!this.currentUser.email || this.currentUser.email.trim() === '') {
      missingItems.push('email');
    }
    if (!this.currentUser.password || this.currentUser.password.trim() === '') {
      missingItems.push('password');
    }
    // Format missing items with bold tags
    const missingText =
      missingItems.length > 0
        ? `Missing: <strong>${missingItems.join(', ')}</strong>.`
        : '';

    this.bannerConfig = {
      type: 'banner-warning',
      icon: 'bi bi-exclamation-triangle-fill',
      title: 'Profile Incomplete',
      message: `Your profile is missing important information. ${missingText} Please complete your profile to access all features.`,
      actionButton: 'Go to Settings',
      buttonClass: 'btn-warning',
      buttonIcon: 'bi bi-gear',
      actionRoute: '/settings',
    };
    this.showBanner = true;

    // console.log('⚠️ Showing incomplete profile banner');
  }

  // Handle banner action button click
  handleBannerAction() {
    if (this.bannerConfig.actionRoute) {
      this.router.navigate([this.bannerConfig.actionRoute]);
      this.dismissBanner();
    }
  }

  // Dismiss the banner
  dismissBanner() {
    this.showBanner = false;
  }

  dismissUpdateBanner() {
    this.showUpdateBanner = false;
  }

  async applyUpdate() {
    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }

      await this.swUpdate.activateUpdate();
      window.location.reload();
    } catch (error) {
      console.error('Error applying service worker update:', error);
    }
  }
}
